import {
  Avatar,
  Box,
  Button,
  CardContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SearchIcon from '@material-ui/icons/Search';
import Skeleton from '@material-ui/lab/Skeleton';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmployeeDTO } from '../models/dto/EmployeeDTO';
import BatchSigningPunchCardDialog from '../pages/PeopleManagement/PunchCard/components/BatchSigningPunchCard/BatchSigningPunchCardDialog';
import EmployeePunchCardDialog from '../pages/PeopleManagement/PunchCard/components/EmployeePunchCard/EmployeePunchCardDialog';
import MedicalCertificatesDialog from '../pages/PeopleManagement/PunchCard/components/MedicalCertificates/MedicalCertificatesCardDialog';
import { AppState } from '../store';
import { selectEmployee } from '../store/actions/employeesActions';
import { EmployeesReducerData } from '../store/reducers/employeesReducer';
import Card from './Card';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    contentContainer: {
      height: 'calc(100% - 128px)',
      paddingTop: 0,
      paddingLeft: 0,
      paddingRight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    cardHeaderContainer: {
      padding: theme.spacing(2),
    },
    counterContainer: {
      backgroundColor: theme.palette.grey[200],
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      boxShadow: '-2px 8px 8px -12px rgba(0,0,0,0.75)',
    },
    listContainer: {
      height: '100%',
      overflowY: 'auto',
    },
    buttonsContainer: {
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    skeletonContainer: {
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    employeeSelectedItem: {
      position: 'absolute',
      top: 6,
      left: 6,
      width: 10,
      height: 10,
    },
  }),
);
interface EmployeeListItemSkeletonProps {
  amount?: number;
}

const EmployeeListItemSkeleton: React.FC<EmployeeListItemSkeletonProps> = ({
  amount = 1,
}) => {
  const classes = useStyles();

  return (
    <>
      {Array.apply(0, Array(amount)).map(function (_, index) {
        return (
          <Grid
            key={index}
            container
            alignItems="center"
            className={classes.skeletonContainer}
          >
            <Grid item xs={2}>
              <Skeleton
                animation="wave"
                variant="circle"
                width={40}
                height={40}
              />
            </Grid>

            <Grid item xs={10}>
              <Skeleton animation="wave" height={15} width="90%" />
              <Skeleton animation="wave" height={15} width="90%" />
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

interface EmployeesListCardProps {
  selectableItemList?: boolean;
  hideAllActionButtonsDefault?: boolean;
  customActionsButtons?: React.ReactNode;
  title: string;
  titleTooltip?: string;
}

const EmployeesListCard: React.FC<EmployeesListCardProps> = ({
  selectableItemList,
  hideAllActionButtonsDefault,
  customActionsButtons,
  title,
  titleTooltip,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [
    openEmployeePunchCardDialog,
    setOpenEmployeePunchCardDialog,
  ] = useState(false);
  const [
    openBatchSigningPunchCardDialog,
    setOpenBatchSigningPunchCardDialog,
  ] = useState(false);
  const [
    openMedicalCertificatesCardDialog,
    setOpenMedicalCertificatesCardDialog,
  ] = useState(false);
  const [employeeResearchInput, setEmployeeResearchInput] = useState('');
  const [openResearchInput, setOpenResearchInput] = useState(false);
  const researchRef = useRef<HTMLInputElement>(null);

  const {
    loading,
    employees,
    employeeSelected,
  }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  const handleEmployeeSelection = (employee: EmployeeDTO) => {
    dispatch(selectEmployee(employee));
    !selectableItemList && setOpenEmployeePunchCardDialog(true);
  };

  useEffect(() => {
    if (openResearchInput) researchRef?.current?.focus();
    else setEmployeeResearchInput('');
  }, [openResearchInput]);

  return (
    <>
      <Card className={classes.card}>
        <Grid
          className={classes.cardHeaderContainer}
          container
          spacing={2}
          alignItems="center"
        >
          <Grid item xs={10}>
            {openResearchInput ? (
              <TextField
                inputRef={researchRef}
                fullWidth
                disabled={!openResearchInput}
                autoComplete="off"
                color="primary"
                placeholder="Nome ou Matrícula"
                value={employeeResearchInput}
                onChange={event => setEmployeeResearchInput(event.target.value)}
              />
            ) : (
              <Typography color="textSecondary" variant="h5">
                {/* Folha de Ponto */}
                {title}
                <Tooltip title={titleTooltip || ''} placement="bottom" arrow>
                  <HelpOutlineIcon color="disabled" />
                </Tooltip>
              </Typography>
            )}
          </Grid>
          <Grid container item xs={12} sm={2}>
            <IconButton
              size="small"
              onClick={() => setOpenResearchInput(!openResearchInput)}
            >
              {openResearchInput ? <CloseIcon /> : <SearchIcon />}
            </IconButton>
          </Grid>
        </Grid>

        <Box className={classes.counterContainer}>
          <Typography color="textSecondary" variant="body1">
            Total:{' '}
          </Typography>
          {loading ? (
            <Skeleton animation="wave" height={25} width="30%" />
          ) : (
            <Typography
              style={{ fontWeight: 'bold', display: 'inline' }}
              color="textSecondary"
              variant="body1"
            >
              {`${employees?.length} `}
              pessoas
            </Typography>
          )}
        </Box>

        <CardContent className={classes.contentContainer}>
          <List className={classes.listContainer}>
            {loading && <EmployeeListItemSkeleton amount={7} />}

            {!loading &&
              employees
                .filter(
                  employee =>
                    employee.nome.includes(
                      employeeResearchInput.toUpperCase(),
                    ) ||
                    String(employee.numeroCadastro).includes(
                      employeeResearchInput,
                    ),
                )
                .sort((a, b) => (a.nome > b.nome ? 1 : -1))
                .map((employee: EmployeeDTO) => {
                  return (
                    <ListItem
                      key={employee.numeroCadastro}
                      divider
                      selected={
                        employeeSelected?.numeroCadastro ===
                          employee.numeroCadastro && selectableItemList
                      }
                      dense
                      button
                      onClick={() => handleEmployeeSelection(employee)}
                    >
                      <FiberManualRecordIcon
                        style={{
                          visibility:
                            employeeSelected?.numeroCadastro ===
                              employee.numeroCadastro && selectableItemList
                              ? 'visible'
                              : 'hidden',
                        }}
                        color="primary"
                        className={classes.employeeSelectedItem}
                      />
                      <ListItemAvatar>
                        <Avatar />
                      </ListItemAvatar>
                      <ListItemText
                        primary={employee.nome}
                        secondary={employee.cargo.descricao}
                      />
                      {!selectableItemList && (
                        <ChevronRightIcon color="disabled" />
                      )}
                    </ListItem>
                  );
                })}
          </List>

          <Box className={classes.buttonsContainer}>
            {!hideAllActionButtonsDefault && (
              <>
                <Button
                  style={{ marginBottom: 16 }}
                  fullWidth
                  onClick={() => setOpenBatchSigningPunchCardDialog(true)}
                  variant="contained"
                  color="primary"
                >
                  Assinar Ponto Digital
                </Button>
                <Button
                  fullWidth
                  onClick={() => setOpenMedicalCertificatesCardDialog(true)}
                  variant="contained"
                  color="primary"
                >
                  Consultar Atestados
                </Button>
              </>
            )}
            {customActionsButtons && customActionsButtons}
          </Box>
        </CardContent>
      </Card>

      {openEmployeePunchCardDialog && (
        <EmployeePunchCardDialog
          open={openEmployeePunchCardDialog}
          handleClose={() => setOpenEmployeePunchCardDialog(false)}
        />
      )}

      <MedicalCertificatesDialog
        open={openMedicalCertificatesCardDialog}
        handleClose={() => setOpenMedicalCertificatesCardDialog(false)}
      />

      {openBatchSigningPunchCardDialog ? (
        <BatchSigningPunchCardDialog
          open={openBatchSigningPunchCardDialog}
          title="Assinar Ponto Digital"
          handleClose={() => setOpenBatchSigningPunchCardDialog(false)}
        />
      ) : null}
    </>
  );
};

export default EmployeesListCard;
